<template>
  <el-dialog
    :title="modalTitle"
    :visible="visible"
    class="responsive invoice-form-modal"
    width="1000px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }">
      <form class="form" @submit.prevent="handleSubmit(onSubmit)">
        <div v-if="mode === 'edit'" class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Mã hóa đơn</label>
              <el-input v-model="form.invoice_code" disabled class="w-100" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Mã PNK</label>
              <ValidationProvider vid="pnk_code" name="Mã PNK" rules="" v-slot="{ errors, classes }">
                <el-input
                  v-model="form.pnk_code"
                  placeholder="Nhập mã phiếu nhập kho"
                  class="w-100"
                  :class="classes"
                />
                <div class="fv-plugins-message-container">
                  <div class="fv-help-block text-danger">{{ errors[0] }}</div>
                </div>
              </ValidationProvider>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Ngày mua <span class="text-danger">*</span></label>
              <ValidationProvider vid="purchase_date" name="Ngày mua" rules="required" v-slot="{ errors, classes }">
                <el-date-picker
                  v-model="form.purchase_date"
                  type="date"
                  value-format="yyyy-MM-dd"
                  format="dd-MM-yyyy"
                  placeholder="Chọn ngày mua"
                  class="w-100"
                  :class="classes"
                />
                <div class="fv-plugins-message-container">
                  <div class="fv-help-block text-danger">{{ errors[0] }}</div>
                </div>
              </ValidationProvider>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Trạng thái thanh toán</label>
              <el-input :value="paymentStatusLabel" disabled class="w-100" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Trạng thái nhập kho</label>
              <el-input :value="importStatusLabel" disabled class="w-100" />
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>Ghi chú</label>
          <el-input
            v-model="form.note"
            type="textarea"
            :rows="3"
            placeholder="Nhập ghi chú"
            class="w-100"
          />
        </div>

        <div class="product-section">
          <h5 class="font-weight-bold mb-3">Danh sách sản phẩm</h5>

          <div class="table-responsive product-table-wrapper">
            <table class="table table-bordered table-vertical-center mb-0">
              <colgroup>
                <col class="product-index" />
                <col class="product-name" />
                <col class="product-quantity" />
                <col class="product-price" />
                <col class="product-total" />
                <col class="product-action" />
              </colgroup>
              <thead class="bg-light">
                <tr>
                  <th class="product-index text-center">STT</th>
                  <th class="product-name">Sản phẩm <span class="text-danger">*</span></th>
                  <th class="product-quantity">Số lượng <span class="text-danger">*</span></th>
                  <th class="product-price">Đơn giá <span class="text-danger">*</span></th>
                  <th class="product-total">Thành tiền</th>
                  <th class="product-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, index) in form.products" :key="product.rowKey">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td>
                    <ValidationProvider
                      :vid="productFieldVid(index, 'name')"
                      name="Sản phẩm"
                      rules="required"
                      v-slot="{ errors, classes }"
                    >
                      <el-autocomplete
                        v-model="product.name"
                        :fetch-suggestions="(query, cb) => queryProductSuggestions(query, cb, index)"
                        value-key="name"
                        :trigger-on-focus="true"
                        :placeholder="productOptionsLoading ? 'Đang tải sản phẩm...' : 'Chọn sản phẩm'"
                        class="w-100"
                        :class="getProductNameClasses(classes, index)"
                        @focus="saveProductSnapshot(product)"
                        @blur="handleProductBlur(product)"
                        @input="handleProductInput(product)"
                        @select="handleProductSelection(product, $event)"
                      >
                        <template slot-scope="{ item }">
                          <div
                            class="product-suggestion"
                            :class="{ 'is-disabled': item.disabled }"
                            @click="onSuggestionClick(item, $event)"
                          >
                            {{ item.name }}
                          </div>
                        </template>
                      </el-autocomplete>
                      <div class="fv-plugins-message-container">
                        <div class="fv-help-block text-danger">{{ errors[0] || duplicateProductError(index) || unselectedProductError(product) }}</div>
                      </div>
                    </ValidationProvider>
                  </td>
                  <td>
                    <ValidationProvider
                      :vid="productFieldVid(index, 'quantity')"
                      name="Số lượng"
                      rules="required|integer|min_value:1"
                      v-slot="{ errors, classes }"
                    >
                      <el-input-number
                        v-model="product.quantity"
                        :precision="0"
                        controls-position="right"
                        class="w-100"
                        :class="classes"
                        :disabled="isExistingProductRow(product)"
                        @input.native="handleQuantityInput(product, $event)"
                        @change="handleQuantityChange(product)"
                      />
                      <div class="fv-plugins-message-container">
                        <div class="fv-help-block text-danger">{{ errors[0] }}</div>
                      </div>
                    </ValidationProvider>
                  </td>
                  <td>
                    <ValidationProvider
                      :vid="productFieldVid(index, 'unit_price')"
                      name="Đơn giá"
                      :rules="unitPriceRules(product)"
                      v-slot="{ errors, classes }"
                    >
                      <money
                        v-model="product.unit_price"
                        v-bind="money"
                        placeholder="Nhập đơn giá"
                        class="form-control text-right w-100"
                        :class="classes"
                        @input="handleUnitPriceInput(product)"
                      />
                      <div class="fv-plugins-message-container">
                        <div class="fv-help-block text-danger">{{ errors[0] }}</div>
                      </div>
                    </ValidationProvider>
                  </td>
                  <td class="font-weight-bold">{{ formatCurrency(product.quantity * product.unit_price) }}</td>
                  <td class="text-center">
                    <button
                      type="button"
                      class="btn btn-icon btn-xs btn-outline-danger"
                      :title="isExistingProductRow(product) ? 'Không thể xóa sản phẩm đã có trong hóa đơn' : 'Xóa sản phẩm'"
                      :disabled="form.products.length === 1 || isExistingProductRow(product)"
                      @click="removeProduct(index)"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <button type="button" class="btn btn-light-primary btn-sm font-weight-bold mt-3" @click="addProduct">
            <i class="el-icon-plus"></i> Thêm sản phẩm
          </button>

          <div class="d-flex justify-content-end mt-3">
            <div class="invoice-total">
              Tổng tiền dự kiến:
              <span class="text-primary font-weight-bold ml-2">{{ formatCurrency(invoiceTotal) }}</span>
            </div>
          </div>
        </div>

        <div v-if="submitError" class="alert alert-custom alert-light-danger mt-4 mb-0 py-3">
          <div class="alert-text">{{ submitError }}</div>
        </div>

        <footer class="d-flex justify-content-end pt-5">
          <button
            type="button"
            class="btn btn-secondary font-weight-bold mr-2"
            :disabled="submitting"
            @click="handleClose"
          >
            Đóng
          </button>
          <button type="submit" class="btn btn-primary font-weight-bold" :disabled="invalid || hasDuplicateProducts || hasUnselectedProducts || submitting">
            <i v-if="submitting" class="fas fa-spinner fa-spin mr-1"></i>
            {{ submitting ? "Đang lưu..." : mode === "create" ? "Thêm mới" : "Cập nhật" }}
          </button>
        </footer>
      </form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
import { Money } from "v-money";

export default {
  name: "InvoiceFormModal",
  components: {
    Money,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ["create", "edit"].includes(value),
    },
    initialData: {
      type: Object,
      default: null,
    },
    productOptions: {
      type: Array,
      default: () => [],
    },
    productOptionsLoading: {
      type: Boolean,
      default: false,
    },
    submitHandler: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      rowCounter: 0,
      form: this.getInitialForm(),
      submitting: false,
      submitError: "",
      money: {
        decimal: ",",
        thousands: ",",
        prefix: "",
        suffix: " đ",
        precision: 0,
        masked: false,
      },
    };
  },
  computed: {
    modalTitle() {
      return this.mode === "create" ? "Thêm mới hóa đơn" : "Sửa hóa đơn";
    },
    paymentStatusLabel() {
      const labels = {
        0: "Chưa thanh toán",
        1: "Thanh toán 50%",
        2: "Đã thanh toán",
      };
      return labels[Number(this.form.payment_status)] || labels[0];
    },
    importStatusLabel() {
      const labels = {
        0: "Chưa nhập",
        1: "Nhập một phần",
        2: "Nhập toàn bộ",
      };
      return labels[Number(this.form.import_status)] || labels[0];
    },
    invoiceTotal() {
      return this.form.products.reduce(
        (total, item) => total + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0),
        0
      );
    },
    duplicateProductIndexes() {
      const duplicateIndexes = {};
      const usedNames = {};
      const usedIds = {};

      this.form.products.forEach((product, index) => {
        const name = this.normalizeProductName(product.name);
        if (name) {
          if (usedNames[name] !== undefined) {
            duplicateIndexes[index] = true;
          } else {
            usedNames[name] = index;
          }
        }

        if (product.product_id != null) {
          if (usedIds[product.product_id] !== undefined) {
            duplicateIndexes[index] = true;
          } else {
            usedIds[product.product_id] = index;
          }
        }
      });

      return duplicateIndexes;
    },
    hasUnselectedProducts() {
      return this.form.products.some((product) => this.isUnselectedProduct(product));
    },
    hasDuplicateProducts() {
      return Object.keys(this.duplicateProductIndexes).length > 0;
    },
  },
  watch: {
    visible(value) {
      if (value) this.syncForm();
    },
    initialData: {
      handler() {
        if (this.visible) this.syncForm();
      },
      deep: true,
    },
  },
  methods: {
    createProductRow(product = {}) {
      this.rowCounter += 1;
      const hasProduct = product.product_id != null || String(product.name || product.product_name || "").trim();
      return {
        rowKey: `invoice-product-${this.rowCounter}`,
        id: product.id ?? null,
        product_id: product.product_id ?? null,
        name: product.name || product.product_name || "",
        quantity: Number(product.quantity) || 1,
        unit_price: hasProduct ? (Number(product.unit_price) || 0) : "",
      };
    },
    getInitialForm() {
      return {
        invoice_code: "",
        pnk_code: "",
        purchase_date: "",
        payment_status: 0,
        import_status: 0,
        note: "",
        products: [this.createProductRow()],
      };
    },
    syncForm() {
      this.submitError = "";
      this.submitting = false;
      if (this.mode === "edit" && this.initialData) {
        this.form = {
          invoice_code: this.initialData.invoice_code || "",
          pnk_code: this.initialData.pnk_code || "",
          purchase_date: this.initialData.purchase_date || "",
          payment_status: this.initialData.payment_status ?? 0,
          import_status: this.initialData.import_status ?? 0,
          note: this.initialData.note || "",
          products: (this.initialData.products || []).map((item) => this.createProductRow(item)),
        };
        if (this.form.products.length === 0) this.form.products.push(this.createProductRow());
      } else {
        this.form = this.getInitialForm();
      }

      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.reset();
      });
    },
    addProduct() {
      this.form.products.push(this.createProductRow());
    },
    isExistingProductRow(product) {
      return this.mode === "edit" && product.id != null;
    },
    removeProduct(index) {
      if (this.form.products.length <= 1) return;
      this.form.products.splice(index, 1);
    },
    queryProductSuggestions(queryString, callback, currentIndex) {
      const keyword = String(queryString || "").trim().toLowerCase();
      const selectedProductIds = this.form.products
        .filter((_, index) => index !== currentIndex)
        .map((item) => item.product_id)
        .filter((id) => id !== null && id !== undefined);

      let suggestions = this.productOptions.map((item) => ({
        ...item,
        disabled: selectedProductIds.includes(item.id),
      }));

      if (keyword) {
        suggestions = suggestions.filter((item) => String(item.name || "").toLowerCase().includes(keyword));
      }
      callback(suggestions);
    },
    saveProductSnapshot(product) {
      product._snapshotName = product.name;
      product._snapshotProductId = product.product_id;
      product._snapshotUnitPrice = product.unit_price;
    },
    onSuggestionClick(item, event) {
      if (item.disabled) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    handleProductInput(product) {
      product.product_id = null;
      product.unit_price = 0;
    },
    handleProductSelection(product, selectedProduct) {
      if (selectedProduct?.disabled) {
        product.name = product._snapshotName ?? "";
        product.product_id = product._snapshotProductId ?? null;
        product.unit_price = product._snapshotUnitPrice ?? 0;
        return;
      }

      product.name = selectedProduct.name;
      product.product_id = selectedProduct.id;
      product.unit_price = Number(selectedProduct.price) || 0;
      this.saveProductSnapshot(product);
    },
    handleQuantityInput(product, event) {
      const value = event?.target?.value;
      const quantity = Number(String(value || "").replace(/[^\d]/g, ""));
      product.quantity = quantity || null;
    },
    handleQuantityChange(product) {
      const quantity = Number(product.quantity);
      product.quantity = Number.isInteger(quantity) && quantity > 0 ? quantity : null;
    },
    handleUnitPriceInput(product) {
      if (Number(product.unit_price) < 0) {
        this.$nextTick(() => {
          product.unit_price = 0;
        });
      }
    },
    unitPriceRules(product) {
      const hasProduct = product.product_id != null || String(product.name || "").trim();

      return hasProduct ? "required|integer|min_value:1" : "";
    },
    normalizeProductName(name) {
      return String(name || "").trim().toLowerCase();
    },
    productFieldVid(index, field) {
      return `products.${index}.${field}`;
    },
    isUnselectedProduct(product) {
      return (
        !this.isExistingProductRow(product) &&
        product.product_id == null &&
        String(product.name || "").trim() !== ""
      );
    },
    unselectedProductError(product) {
      return this.isUnselectedProduct(product) ? "Vui lòng chọn sản phẩm từ danh sách" : "";
    },
    handleProductBlur(product) {
      setTimeout(() => {
        if (this.isUnselectedProduct(product)) {
          product.name = "";
          product.unit_price = "";
        }
      }, 200);
    },
    duplicateProductError(index) {
      return this.duplicateProductIndexes[index] ? "Sản phẩm đã tồn tại" : "";
    },
    getProductNameClasses(classes, index) {
      return this.duplicateProductError(index)
        ? { ...classes, "is-valid": false, "is-invalid": true }
        : classes;
    },
    formatCurrency(value) {
      return `${new Intl.NumberFormat("vi-VN").format(Number(value) || 0)} đ`;
    },
    buildPayload() {
      const payload = {
        purchase_date: this.form.purchase_date,
        note: String(this.form.note || "").trim() || null,
        payment_status: Number(this.form.payment_status) || 0,
        products: this.form.products.map((product) => ({
          product_id: product.product_id ?? null,
          name: String(product.name || "").trim(),
          quantity: Number(product.quantity),
          unit_price: Number(product.unit_price),
        })),
      };

      if (this.mode === "edit") {
        payload.id = this.initialData.id;
        payload.pnk_code = String(this.form.pnk_code || "").trim() || null;
      }

      return payload;
    },
    normalizeValidationErrors(errors = {}) {
      return Object.keys(errors).reduce((normalizedErrors, field) => {
        const normalizedField = field.replace(/^products\.(\d+)\.product_id$/, "products.$1.name");
        normalizedErrors[normalizedField] = errors[field];
        return normalizedErrors;
      }, {});
    },
    async onSubmit() {
      if (this.submitting || this.hasDuplicateProducts || this.hasUnselectedProducts) return;
      this.submitting = true;
      this.submitError = "";

      const payload = this.buildPayload();

      try {
        if (this.submitHandler) await this.submitHandler(payload);
        this.$emit("submit", payload);
        this.handleClose();
      } catch (error) {
        const validationErrors =
          error?.data?.message_validate_form ||
          error?.data?.data?.message_validate_form ||
          error?.response?.data?.message_validate_form ||
          error?.response?.data?.data?.message_validate_form;

        if (validationErrors && this.$refs.form) {
          this.$refs.form.setErrors(this.normalizeValidationErrors(validationErrors));
        }
        this.submitError = error?.message || "Không thể lưu hóa đơn";
      } finally {
        this.submitting = false;
      }
    },
    handleClose() {
      this.form = this.getInitialForm();
      this.submitting = false;
      this.submitError = "";
      if (this.$refs.form) this.$refs.form.reset();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.product-section {
  border-top: 1px solid #ebedf3;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
}

.product-table-wrapper {
  border-radius: 0.25rem;
}

.product-table-wrapper table {
  table-layout: fixed;
  width: 100%;
}

.product-table-wrapper >>> tbody td {
  vertical-align: top;
}

.product-table-wrapper >>> tbody td:first-child,
.product-table-wrapper >>> tbody td:nth-last-child(2),
.product-table-wrapper >>> tbody td:last-child {
  padding-top: 1.25rem;
}

.product-index {
  width: 55px;
}

.product-name {
  width: 200px;
}

.product-quantity {
  width: 150px;
}

.product-price {
  width: 150px;
}

.product-total {
  width: 100px;
}

.product-action {
  width: 55px;
}

.invoice-total {
  font-size: 1rem;
}

.fv-help-block {
  display: block;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  min-height: 1.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-suggestion.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.invoice-form-modal >>> .el-autocomplete-suggestion__list li .product-suggestion.is-disabled {
  pointer-events: auto;
}

.invoice-form-modal >>> .el-autocomplete-suggestion__list li:hover .product-suggestion.is-disabled,
.invoice-form-modal >>> .el-autocomplete-suggestion__list li.highlighted .product-suggestion.is-disabled {
  background-color: transparent;
}

@media screen and (max-width: 767px) {
  .product-table-wrapper {
    overflow-x: auto;
  }

  .product-table-wrapper table {
    min-width: 800px;
  }
}
</style>
